import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';

@Component
export class StockStatus extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop selectedVariant!: Shopify.Liquid.Variant;
    @Prop storeAvailabilities!: Record<string, { locationId: number; available: boolean; pickUpTime?: string }[]>;

    @Ref inStock!: HTMLElement;
    @Ref inStockText!: HTMLElement;
    @Ref dispatch!: HTMLElement;
    @Ref dispatchText!: HTMLElement;
    @Ref outOfStock!: HTMLElement;

    private activeItemClass: string = 'product-stock-status-item--active';
    private activeItemTextClass: string = 'product-stock-status-item__sub-text--active';

    init() {
        this.updateVisibility(this.selectedVariant);
        // Find the closest container that has both StockStatus and ProductForm
        const productWrap = this.closest('.product-wrap-js');
        if (productWrap) {
            const productOptionPicker = productWrap.querySelector('product-option-picker');
            if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
                const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;
                // eslint-disable-next-line max-len
                this.$subscribe(productOptionPickerComponent.onSelectedVariantChange, (variant: Shopify.Liquid.Variant|null) => {
                    if (variant) {
                        this.selectedVariant = variant;
                        this.updateVisibility(this.selectedVariant);
                        this.fetchStoreAvailabilities(variant.id);
                    } else {
                        // No matching variant for the selected options — show out of stock
                        this.showOutOfStock();
                    }
                });
            }
        }
    }

    private fetchStoreAvailabilities(variantId: number) {
        const productUrl = this.getAttribute('product-url');
        const sectionId = this.getAttribute('section-id');

        if (!productUrl || !sectionId) {return;}

        const url = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;

        fetch(url)
            .then((response) => response.text())
            .then((html) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const stockStatusEl = doc.querySelector('stock-status');
                if (!stockStatusEl) {return;}

                const raw = stockStatusEl.getAttribute('store-availabilities');
                if (!raw) {return;}

                this.setAttribute('store-availabilities', raw);
                try {
                    this.storeAvailabilities = JSON.parse(decodeURIComponent(raw.replace(/&amp;/g, '&')));
                    this.updateVisibility(this.selectedVariant);
                } catch (_) {
                    // ignore parse errors
                }
            })
            .catch(() => {
                // silently ignore network errors
            });
    }

    private updatePickupTime(variantId: number) {
        const locations = this.storeAvailabilities?.[String(variantId)];
        const pickupTime = locations?.find((loc) => loc.available)?.pickUpTime
            ?? locations?.[0]?.pickUpTime
            ?? '';
        for (const el of this.querySelectorAll('[data-ref="pickupTime"]')) {
            el.textContent = pickupTime;
        }
    }

    updateVisibility(variant: Shopify.Liquid.Variant) {
        this.updatePickupTime(variant.id);

        // Check variant is in stock
        if (variant.available) {

            // Check store availabilities for this variant
            const locations = this.storeAvailabilities?.[String(variant.id)];

            this.handleItemActiveClass(this.outOfStock, 'hide');

            if (locations === undefined) {
                // Data not yet fetched — default to In Stock, fetch will correct if needed
                this.handleItemActiveClass(this.inStock, 'show');
                this.handleItemActiveClass(this.dispatch, 'hide');
            } else if (locations.some((loc) => loc.available)) {
                // At least one location has stock — show In Stock
                this.handleItemActiveClass(this.inStock, 'show');
                this.handleItemActiveClass(this.dispatch, 'hide');
            } else {
                // No store location has stock — show Dispatch
                this.handleItemActiveClass(this.inStock, 'hide');
                this.handleItemActiveClass(this.dispatch, 'show');
            }
        } else {

            // Show out of stock item
            this.showOutOfStock();

            // Do not continue
            return;
        }

        // Update in stock / dispatch text
        this.inStockText.textContent = 'In Stock.';
        this.dispatchText.textContent = 'In Stock.';

        // Check if product is for pre-order
        if (this.product.tags && this.product.tags.includes('preorder')) {

            // Only change stock status if variant quantity is <= 0
            if (variant.inventory_quantity && variant.inventory_quantity > 0) {
                return;
            }

            // Update in stock text
            this.inStockText.textContent = 'Pre-order';
            this.dispatchText.textContent = 'Pre-order';

            // Show pre-order only message
            this.removeAllItemTextActiveClasses();
            this.handleItemTextActiveClassByRef('preOrderOnly', 'show');

            // Do not continue
            return;
        }

        // Check if product is pickup only
        if (this.product.tags && this.product.tags.includes('pickup-only')) {

            // Show pick only message
            this.removeAllItemTextActiveClasses();
            this.handleItemTextActiveClassByRef('collectionOnly', 'show');

            // Do not continue
            return;
        }

        // Check if product has a shipping restriction
        if (this.product.tags && this.product.tags.includes('shipping-restriction')) {

            // Show shipping restriction message
            this.removeAllItemTextActiveClasses();
            this.handleItemTextActiveClassByRef('countryRestriction', 'show');

            // Do not continue
            return;
        } else {

            // Show no shipping restriction message
            this.removeAllItemTextActiveClasses();
            this.handleItemTextActiveClassByRef('noCountryRestriction', 'show');

            // Do not continue
            return;
        }
    }

    showOutOfStock() {
        this.handleItemActiveClass(this.inStock, 'hide');
        this.handleItemActiveClass(this.dispatch, 'hide');
        this.handleItemActiveClass(this.outOfStock, 'show');
    }

    handleItemActiveClass(item: HTMLElement, action: string) {
        if (action === 'show') {
            item.classList.add(`${this.activeItemClass}`);
        } else {
            item.classList.remove(`${this.activeItemClass}`);
        }
    }

    // Targets all elements with the given data-ref (covers both inStock and dispatch blocks)
    handleItemTextActiveClassByRef(ref: string, action: string) {
        for (const el of this.querySelectorAll(`[data-ref="${ref}"]`)) {
            if (action === 'show') {
                el.classList.add(this.activeItemTextClass);
            } else {
                el.classList.remove(this.activeItemTextClass);
            }
        }
    }

    removeAllItemTextActiveClasses() {
        for (const ref of ['noCountryRestriction', 'countryRestriction', 'collectionOnly', 'preOrderOnly']) {
            for (const el of this.querySelectorAll(`[data-ref="${ref}"]`)) {
                el.classList.remove(this.activeItemTextClass);
            }
        }
    }
}
